import { Controller } from "@hotwired/stimulus"
import EditorJS from "@editorjs/editorjs"
import Header from "@editorjs/header"
import List from "@editorjs/list"
import ImageTool from "@editorjs/image"
import Code from "@editorjs/code"
import Embed from "@editorjs/embed"
import Delimiter from "@editorjs/delimiter"
import Quote from "@editorjs/quote"

// Connects to data-controller="editorjs"
// Renders an Editor.js instance for Guide::Article editing (app/models/guide/article.rb).
// The block JSON is synced into a hidden input on every change so the surrounding
// Rails form_with can submit it as guide_article[editorjs_content].
export default class extends Controller {
  static targets = ["holder", "input"]
  static values = {
    uploadUrl: String,
    content: Object
  }

  connect() {
    this.editor = new EditorJS({
      holder: this.holderTarget,
      data: this.contentValue,
      // Editor.js pads the editable area's bottom by `minHeight` (default 300px)
      // so there's room to click below the last block — this is what makes the
      // editor appear to "end halfway down the screen" with a big empty gap.
      // 0 disables that extra padding; the holder still grows with its content.
      minHeight: 0,
      tools: {
        header: { class: Header, inlineToolbar: true },
        list: { class: List, inlineToolbar: true },
        code: Code,
        embed: Embed,
        delimiter: Delimiter,
        quote: { class: Quote, inlineToolbar: true },
        image: {
          class: ImageTool,
          config: {
            uploader: {
              uploadByFile: (file) => this.uploadByFile(file)
            }
          }
        }
      },
      onChange: () => this.syncContent()
    })
  }

  disconnect() {
    this.editor?.destroy?.()
  }

  async syncContent() {
    if (!this.editor) return

    const data = await this.editor.save()
    this.inputTarget.value = JSON.stringify(data)
  }

  // Editor.js `image` tool uploader.uploadByFile callback — posts directly to the
  // admin/guide/articles/:id/images endpoint (Admin::Guide::ArticlesController#images),
  // which uploads to S3 via Guide::UploadAssetService and returns a public URL.
  async uploadByFile(file) {
    if (!this.uploadUrlValue) {
      const message = "Előbb mentse el a cikket, hogy képet tölthessen fel."
      // Editor.js only surfaces this via a tiny inline error inside the block (easy to
      // miss) and logs a confusing "Block «image» skipped" warning to the console —
      // an explicit alert makes sure the user actually notices why the upload did nothing.
      window.alert(message)
      return { success: 0, message }
    }

    const formData = new FormData()
    formData.append("image", file)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: formData
    })

    return response.json()
  }
}
